<template>
  <div>
    <b-label
        v-if="props.label"
        class="block text-sm font-medium"
        :for="props.id"
    >{{ props.label }}</b-label>

    <!-- Searchable Combobox Implementation -->
    <Combobox v-if="searchable" v-model="selectedItems" by="value" as="div" class="relative" multiple>
      <div ref="comboboxTriggerRef">
        <ComboboxButton class="min-h-[40px] py-[12px] w-full focus:ring-primary px-3 border border-[#373737] rounded-lg">
          <span v-if="selectedItems.length > 0" class="flex flex-wrap gap-2 truncate text-black text-start">
            <div v-for="item in selectedItems" :key="item.value" class="bg-gray-700 border border-gray-600 rounded-full pl-[8px] pr-[4px] py-[2px] flex items-center gap-2">
              <div class="text-xs text-gray-300 whitespace-normal" style="line-height: 1.3; word-wrap: break-word; word-break: break-word;">{{ item.label }}</div>
              <div @click.stop="onDelete(item.value)" class="cursor-pointer rounded-full h-3 w-3 flex items-center justify-center">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.6634 2.58653C10.8729 2.41555 11.1816 2.42768 11.3769 2.62299C11.5722 2.81829 11.5843 3.12705 11.4134 3.33653L11.3769 3.37689L7.75387 6.99994L11.3769 10.623L11.4134 10.6634C11.5843 10.8728 11.5722 11.1816 11.3769 11.3769C11.1816 11.5722 10.8729 11.5843 10.6634 11.4134L10.623 11.3769L6.99996 7.75385L3.37691 11.3769C3.16863 11.5852 2.83129 11.5852 2.62301 11.3769C2.41473 11.1686 2.41473 10.8313 2.62301 10.623L6.24605 6.99994L2.62301 3.37689L2.58655 3.33653C2.41557 3.12705 2.4277 2.81829 2.62301 2.62299C2.81831 2.42768 3.12707 2.41555 3.33655 2.58653L3.37691 2.62299L6.99996 6.24603L10.623 2.62299L10.6634 2.58653Z" fill="#EAEAEA"/>
                </svg>
              </div>
            </div>
          </span>
          <ComboboxInput
              class="w-full bg-transparent placeholder-gray-400 text-start focus:outline-none text-white"
              @change="debouncedFilter"
              :placeholder="selectedItems.length === 0 ? props.placeholder : ''"
          />
        </ComboboxButton>
      </div>
      <!--
        Teleport + fixed positioning: the dropdown is rendered at <body> level to escape
        any parent overflow:hidden/auto container (e.g. BModal's scrollable content area).
        Without this, the dropdown gets clipped inside modals and other overflow containers.
        Position is computed reactively via useElementBounding on the trigger ref.
      -->
      <Teleport to="body">
        <transition>
          <ComboboxOptions
              class="fixed z-[100] max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-300 ring-opacity-5 focus:outline-none"
              :style="comboboxDropdownStyle"
          >
            <template v-if="isGrouped">
              <template v-for="group in filteredItems" :key="group.label">
                <div class="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b">{{ group.label }}</div>
                <ComboboxOption v-for="item in group.options" :key="item.value" :value="item" :disabled="item.disabled || (props.maxItems && selectedItems.length >= props.maxItems && !selectedItems.some(_ => _.value === item.value))" v-slot="{ active, selected, disabled }">
                  <li data-e2e="combobox-option" :class="['relative select-none py-2 pl-10 pr-4', active ? 'bg-blue-100 text-blue-900' : 'text-gray-900', disabled ? 'cursor-not-allowed opacity-50' : '']">
                    <span class="absolute inset-y-0 left-0 flex items-center pl-3">
                      <input type="checkbox" :checked="selected" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 pointer-events-none" />
                    </span>
                    <span :class="['block truncate', selected ? 'font-medium' : 'font-normal']">{{ item.label }}</span>
                  </li>
                </ComboboxOption>
              </template>
            </template>
            <template v-else>
              <ComboboxOption v-for="item in filteredItems" :key="item.value" :value="item" :disabled="item.disabled || (props.maxItems && selectedItems.length >= props.maxItems && !selectedItems.some(_ => _.value === item.value))" v-slot="{ active, selected, disabled }">
                <li data-e2e="combobox-option" :class="['relative select-none py-2 pl-10 pr-4', active ? 'bg-blue-100 text-blue-900' : 'text-gray-900', disabled ? 'cursor-not-allowed opacity-50' : '']">
                  <span class="absolute inset-y-0 left-0 flex items-center pl-3">
                      <input type="checkbox" :checked="selected" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 pointer-events-none" />
                  </span>
                  <span :class="['block truncate', selected ? 'font-medium' : 'font-normal']">{{ item.label }}</span>
                </li>
              </ComboboxOption>
            </template>
          </ComboboxOptions>
        </transition>
      </Teleport>
    </Combobox>

    <!-- Non-searchable Listbox Implementation -->
    <Listbox v-else v-model="selectedItems" by="value" as="div" class="relative" multiple>
      <div ref="listboxTriggerRef">
        <ListboxButton class="min-h-[40px] py-[12px] w-full focus:ring-primary px-3 border border-[#373737] rounded-lg">
          <span v-if="selectedItems.length > 0" class="flex flex-wrap gap-2 truncate text-black text-start">
            <div v-for="item in selectedItems" :key="item.value" class="bg-gray-700 border border-gray-600 rounded-full pl-[8px] pr-[4px] py-[2px] flex items-center gap-2">
              <div class="text-xs text-gray-300 whitespace-normal" style="line-height: 1.3; word-wrap: break-word; word-break: break-word;">{{ item.label }}</div>
              <div @click.stop="onDelete(item.value)" class="cursor-pointer rounded-full h-3 w-3 flex items-center justify-center">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.6634 2.58653C10.8729 2.41555 11.1816 2.42768 11.3769 2.62299C11.5722 2.81829 11.5843 3.12705 11.4134 3.33653L11.3769 3.37689L7.75387 6.99994L11.3769 10.623L11.4134 10.6634C11.5843 10.8728 11.5722 11.1816 11.3769 11.3769C11.1816 11.5722 10.8729 11.5843 10.6634 11.4134L10.623 11.3769L6.99996 7.75385L3.37691 11.3769C3.16863 11.5852 2.83129 11.5852 2.62301 11.3769C2.41473 11.1686 2.41473 10.8313 2.62301 10.623L6.24605 6.99994L2.62301 3.37689L2.58655 3.33653C2.41557 3.12705 2.4277 2.81829 2.62301 2.62299C2.81831 2.42768 3.12707 2.41555 3.33655 2.58653L3.37691 2.62299L6.99996 6.24603L10.623 2.62299L10.6634 2.58653Z" fill="#EAEAEA"/>
                </svg>
              </div>
            </div>
          </span>
          <span v-else-if="props.placeholder" class="block truncate text-gray-400 text-start">{{ props.placeholder }}</span>
          <span v-else class="block truncate text-gray-400 text-start">-</span>
        </ListboxButton>
      </div>
      <!-- Same Teleport pattern as Combobox above (see comment there) -->
      <Teleport to="body">
        <transition>
          <ListboxOptions
              class="fixed z-[100] max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-300 ring-opacity-5 focus:outline-none"
              :style="listboxDropdownStyle"
          >
            <template v-if="isGrouped">
              <template v-for="group in items" :key="group.label">
                <div class="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b">{{ group.label }}</div>
                <ListboxOption v-for="item in group.options" :key="item.value" :value="item" :disabled="item.disabled || (props.maxItems && selectedItems.length >= props.maxItems && !selectedItems.some(_ => _.value === item.value))" v-slot="{ active, selected, disabled }">
                  <li data-e2e="combobox-option" :class="['relative select-none py-2 pl-10 pr-4', active ? 'bg-blue-100 text-blue-900' : 'text-gray-900', disabled ? 'cursor-not-allowed opacity-50' : '']">
                    <span class="absolute inset-y-0 left-0 flex items-center pl-3">
                      <input type="checkbox" :checked="selected" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 pointer-events-none" />
                    </span>
                    <span :class="['block truncate', selected ? 'font-medium' : 'font-normal']">{{ item.label }}</span>
                  </li>
                </ListboxOption>
              </template>
            </template>
            <template v-else>
              <ListboxOption v-for="item in items" :key="item.value" :value="item" :disabled="item.disabled || (props.maxItems && selectedItems.length >= props.maxItems && !selectedItems.some(_ => _.value === item.value))" v-slot="{ active, selected, disabled }">
                <li data-e2e="combobox-option" :class="['relative select-none py-2 pl-10 pr-4', active ? 'bg-blue-100 text-blue-900' : 'text-gray-900', disabled ? 'cursor-not-allowed opacity-50' : '']">
                  <span class="absolute inset-y-0 left-0 flex items-center pl-3">
                      <input type="checkbox" :checked="selected" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 pointer-events-none" />
                  </span>
                  <span :class="['block truncate', selected ? 'font-medium' : 'font-normal']">{{ item.label }}</span>
                </li>
              </ListboxOption>
            </template>
          </ListboxOptions>
        </transition>
      </Teleport>
    </Listbox>
  </div>
</template>

<script lang="ts" setup>
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/vue'
import { computed, ref, watch } from 'vue'
import { useField } from "vee-validate";
import BLabel from '../BLabel.vue';
import { useDebounceFn, useElementBounding } from '@vueuse/core';

export interface SelectItem {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectGroup {
  label: string;
  options: SelectItem[];
}

interface Props {
  items: SelectItem[] | SelectGroup[];
  id?: string;
  label?: string;
  placeholder?: string;
  name?: string;
  modelValue: SelectItem[] | [];
  searchable?: boolean;
  rules?: string | Record<string, any> | Function;
  top?: boolean;
  maxItems?: number;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  modelValue: [],
  id: () => `select-${new Date().getTime().toString()}`,
  name: () => `chip-input-${Date.now()}`,
  searchable: false,
})

// Dropdown positioning: we use Teleport to render the options at <body> level
// so they escape any parent overflow:hidden/auto (e.g. inside BModal).
// useElementBounding tracks the trigger's viewport position reactively,
// and the computed styles position the fixed dropdown right below (or above) it.
const comboboxTriggerRef = ref<HTMLElement | null>(null);
const listboxTriggerRef = ref<HTMLElement | null>(null);

const comboboxBounding = useElementBounding(comboboxTriggerRef);
const listboxBounding = useElementBounding(listboxTriggerRef);

const comboboxDropdownStyle = computed(() => {
  const { top, bottom, left, width } = comboboxBounding;
  return props.top
    ? { bottom: `${window.innerHeight - top.value + 4}px`, left: `${left.value}px`, width: `${width.value}px` }
    : { top: `${bottom.value + 4}px`, left: `${left.value}px`, width: `${width.value}px` };
});

const listboxDropdownStyle = computed(() => {
  const { top, bottom, left, width } = listboxBounding;
  return props.top
    ? { bottom: `${window.innerHeight - top.value + 4}px`, left: `${left.value}px`, width: `${width.value}px` }
    : { top: `${bottom.value + 4}px`, left: `${left.value}px`, width: `${width.value}px` };
});

const nameRef = toRef(props, 'name');
const { value, errorMessage, setValue } = useField(nameRef, props.rules, {
  initialValue: props.modelValue
});

const error = computed(() => errorMessage.value);

const emit = defineEmits<{
  (e: 'update:modelValue', value: SelectItem[] | []): void
}>()

const isGrouped = computed(() => {
  return props.items.length > 0 && 'options' in props.items[0]
})

const onDelete = (value) => {
  const newSelection = selectedItems.value.filter(_ => _.value !== value);
  emit('update:modelValue', newSelection)
}

const filteredItems = ref(props.items);

watch(() => props.items, (newItems) => {
  filteredItems.value = newItems;
});

const debouncedFilter = (event) => {
  const query = event.target.value;
  if (query === '') {
    filteredItems.value = props.items;
    return;
  }

  if (isGrouped.value) {
    filteredItems.value = (props.items as SelectGroup[])
        .map(group => {
          const filteredOptions = group.options.filter(option =>
              option.label.toLowerCase().includes(query.toLowerCase())
          );
          return { ...group, options: filteredOptions };
        })
        .filter(group => group.options.length > 0);
  } else {
    filteredItems.value = (props.items as SelectItem[]).filter(item =>
        item.label.toLowerCase().includes(query.toLowerCase())
    );
  }
};

// Helper to get all items (flat or grouped)
const getAllItems = (): SelectItem[] => {
  if (isGrouped.value) {
    return (props.items as SelectGroup[]).flatMap(group => group.options);
  }
  return props.items as SelectItem[];
};

// Sync selectedItems with modelValue, ensuring they reference the same objects from items
const selectedItems = computed({
  get: () => {
    if (!props.modelValue || props.modelValue.length === 0) {
      return [];
    }

    // Find matching items from the items prop by value
    const allItems = getAllItems();

    // If no items loaded yet, return modelValue directly (for restored values)
    if (allItems.length === 0) {
      return props.modelValue;
    }

    const modelValues = props.modelValue.map(item => item.value);
    const matched = allItems.filter(item => modelValues.includes(item.value));

    // If no matches found, return modelValue directly (values might not be in items list yet)
    return matched.length > 0 ? matched : props.modelValue;
  },
  set: (value: SelectItem[] | []) => {
    emit('update:modelValue', value)
  }
})
</script>
